import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, ArrowRightIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { getThemeStyles } from '../styles/theme';
import { isDemoMode } from '../utils/demo';

const KyndChoice: React.FC = () => {
  const [isDarkMode] = React.useState(false);
  const theme = getThemeStyles(isDarkMode);

  // Demo mock data (Clients table)
  const demo = isDemoMode() || (typeof window !== 'undefined' && window.location.hostname.includes('kyndly.xyz'));
  const clients = [
    { name: '*DEMO', url: 'https://ichra.kyndchoice.com/?client=DEMO2' },
    { name: 'Apex Solutions Group', url: 'https://ichra.kyndchoice.com/?client=ASG' },
    { name: 'Atlas Holdings', url: 'https://ichra.kyndchoice.com/?client=Atlas' },
    { name: 'Blueprint Advisory', url: 'https://ichra.kyndchoice.com/?client=BlueAdv' },
    { name: 'Catalyst Innovations', url: 'https://ichra.kyndchoice.com/?client=CatIn' },
    { name: 'Cornerstone Networks', url: 'https://ichra.kyndchoice.com/?client=CRNNet' },
    { name: 'Eclipse Corp', url: 'https://ichra.kyndchoice.com/?client=ECCorp' },
    { name: 'Horizon Partners', url: 'https://ichra.kyndchoice.com/?client=HP' },
    { name: 'Meridian Technologies', url: 'https://ichra.kyndchoice.com/?client=MT' },
    { name: 'Nexus Systems', url: 'https://ichra.kyndchoice.com/?client=NexSy' },
    { name: 'Phoenix Analytics', url: 'https://ichra.kyndchoice.com/?client=PXAX' },
    { name: 'Pinnacle Dynamics', url: 'https://ichra.kyndchoice.com/?client=Pinn' },
    { name: 'Prism Digital', url: 'https://ichra.kyndchoice.com/?client=Prism' },
    { name: 'Quantum Ventures', url: 'https://ichra.kyndchoice.com/?client=QuantV' },
    { name: 'Sterling Innovations', url: 'https://ichra.kyndchoice.com/?client=Sterling' },
    { name: 'Summit Enterprises', url: 'https://ichra.kyndchoice.com/?client=SumEnter' },
    { name: 'Titan Strategies', url: 'https://ichra.kyndchoice.com/?client=TitanStrat' },
  ];

  return (
    <div className={`min-h-screen ${theme.layout.container}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {demo ? (
          <div className={`${theme.card} p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-slate-800">Clients</h1>
              <div className="flex items-center gap-3">
                <button className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 flex items-center gap-2">
                  <DocumentDuplicateIcon className="h-4 w-4" /> Export all clients
                </button>
                <button className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm">New client</button>
              </div>
            </div>
            <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
              <input id="inc-archived" type="checkbox" className="h-4 w-4" />
              <label htmlFor="inc-archived">Include archived</label>
            </div>
            <div className="border rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map((c) => (
                    <tr key={c.name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-seafoam hover:underline">{c.url}</a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <button className="px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs">Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`${theme.card} p-8 text-center`}
        >
          <div className="flex justify-center mb-6">
            <div className={`${theme.layout.section} rounded-full p-4`}>
              <SparklesIcon className="text-amber-500" width={48} height={48} />
            </div>
          </div>

          <h1 className={`${theme.typography.h1} mb-4`}>Coming Soon</h1>
          <p className={`${theme.typography.body} mb-8 max-w-2xl mx-auto`}>
            We're working on something exciting! KyndChoice will help you make informed decisions about your benefits with personalized recommendations and expert guidance.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className={`${theme.card} p-6`}>
              <h3 className={`${theme.typography.h3} mb-3`}>Personalized Recommendations</h3>
              <p className={theme.typography.body}>
                Get tailored benefit suggestions based on your unique needs and preferences.
              </p>
            </div>
            <div className={`${theme.card} p-6`}>
              <h3 className={`${theme.typography.h3} mb-3`}>Expert Guidance</h3>
              <p className={theme.typography.body}>
                Access professional advice to help you make the best choices for your situation.
              </p>
            </div>
            <div className={`${theme.card} p-6`}>
              <h3 className={`${theme.typography.h3} mb-3`}>Easy Comparison</h3>
              <p className={theme.typography.body}>
                Compare different benefit options side by side to make informed decisions.
              </p>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`${theme.button.primary} flex items-center justify-center space-x-2 mx-auto`}
          >
            <span>Get Notified When Available</span>
            <ArrowRightIcon className="text-white" width={20} height={20} />
          </motion.button>
        </motion.div>
        )}
      </div>
    </div>
  );
};

export default KyndChoice; 